<template>
  <div>
    <button
      type="button"
      class="btn-codigo-descuento w-100 p-15 d-flex align-items-center justify-content-between"
      @click="showModal = true">
      <div class="d-flex align-items-center">
        <CustomIcon :icon="codigoDescuentoIcon" size="16px" class="mr-10" />
        <span class="semibold">{{ $t('codigoDescuento.addCodigoDescuento') }}</span>
      </div>
      <CustomIcon :icon="chevronRightIcon" size="16px" />
    </button>

    <Teleport to="body">
      <ModalBase
        :show="showModal"
        :style-vars="styleVars"
        :title="$t('reserva.descuentoCod')"
        hide-submit
        hide-cancel
        @on-close="handleClose">
        <template #body>
          <div class="row">
            <div class="input-wrapper">
              <input
                v-model="codigoDescuento"
                type="text"
                class="form-control"
                :placeholder="$t('codigoDescuento.placeholder')"
                required />
            </div>
          </div>
        </template>
        <template #footer>
          <div class="d-flex gap-3 justify-content-end w-100 mt-10">
            <MyGocuButton secondary :label="$t('general.cancelar')" @click="handleClose" />
            <MyGocuButton :label="$t('general.aplicarDescuento')" :disabled="loading" @click="validarCodigo" />
          </div>
        </template>
      </ModalBase>
    </Teleport>
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import ModalBase from '@/components/modales/ModalBase.vue';
  import ViajesGrupoApiService from '@/services/api/ViajesGrupoApiService';
  import { alertAutoCloseSucessHtml, alertAutoCloseWarningWithoutCallback } from '@/services/utils/AlertUtils';
  import { useI18n } from 'vue-i18n';
  import CustomIcon from '@/components/CustomIcon.vue';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import codigoDescuentoIcon from '@/assets/icons/codigo-descuento.svg';
  import chevronRightIcon from '@/assets/icons/chevron-right.svg';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import { landingStyleVars } from '@/helpers/landingStyleUtils';
  import { computed } from 'vue';

  const props = defineProps({
    viajeId: {
      type: String,
      default: null,
    },
    reservasIds: {
      type: Array,
      default: () => [],
    },
    noTitle: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const emits = defineEmits(['descuentoAplicado']);
  const codigoDescuento = ref(null);
  const loading = ref(false);
  const showModal = ref(false);
  const { t } = useI18n();
  const loadingStore = useLoadingStore();
  const storeHabitacion = informacionHabitacion();
  const styleVars = computed(() =>
    storeHabitacion.getViaje?.style ? landingStyleVars(storeHabitacion.getViaje.style) : null
  );

  function handleClose() {
    showModal.value = false;
    codigoDescuento.value = null;
  }

  async function validarCodigo() {
    try {
      loadingStore.show();
      let res = await ViajesGrupoApiService.validarDescuento(props.viajeId, codigoDescuento.value, props.reservasIds);
      alertAutoCloseSucessHtml(3000, ` <strong><p class="text-primary">${res.descripcion}</p></strong>`);

      emits('descuentoAplicado', { codigo: codigoDescuento.value, ...res });
      handleClose();
    } catch (err) {
      alertAutoCloseWarningWithoutCallback(
        2000,
        t('reserva.descuentoCodError', {
          codigo: codigoDescuento.value,
        })
      );
    } finally {
      loadingStore.hide();
    }
  }
</script>

<style scoped lang="scss">
  .btn-codigo-descuento {
    background: transparent;
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
    border-radius: var(--bs-border-radius-xl);
    color: var(--landing-text-color, var(--bs-primary));
    cursor: pointer;

    &:hover {
      background-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1);
    }
  }

  .input-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
    border-radius: var(--bs-border-radius-lg);
    overflow: hidden;

    .form-control {
      border-radius: var(--bs-border-radius-lg);
      background: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.02);
      border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
      box-shadow: 0px 0px 3px 0px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.35) inset;
      height: 40px;
      width: 100%;
    }
  }

  [type='text'],
  input[type='text']::placeholder {
    text-align: left;
    left: 2px;
    top: 0;
  }

  input[type='text']::placeholder {
    font-size: 16px;
    color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.4);
    font-style: italic;
  }

  @media screen and (max-width: 768px) {
    .form-control {
      font-size: 12px;
    }
  }

  :deep(.modal-container) {
    width: 600px !important;
    min-width: unset !important;

    @media screen and (max-width: 768px) {
      width: 95% !important;
    }
  }

  :deep(.btn-cancelar) {
    height: 40px;
    box-shadow: none;
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.4);
    padding: 10px 24px 10px 24px;
    border-radius: 20px;
    background-color: transparent !important;
    color: var(--landing-text-color, var(--bs-primary)) !important;
  }

  :deep(.modal-mask) {
    z-index: 1007;
  }
</style>
